import React, { useState, useRef } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import "./Modal.css";
import "./LoginModal.css";
import img from "../../assets/images/logo.jpg";
import DataBase from "../../dataBase/liquidGuides";
import User from "../../Models/User";
import { useDispatch } from "react-redux";
import { slice1Actions } from "../../store/reducer";
import ReactLoading from "react-loading";
import AlertModal from "./AlertModal";

const icoLogin     = solid("right-to-bracket");
const icoRegister  = solid("user-plus");
const icoForgotPw  = solid("key");
const icoEye       = solid("eye");
const icoEyeSlash  = solid("eye-slash");

const LoginModal = (props) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [AlertModalShow, setAlertModalShow] = useState(false);
  const [AlertModalTitle, setAlertModalTitle] = useState("");
  const [AlertModalMessage, setAlertModalMessage] = useState("");
  const [AlertModalButtons, setAlertModalButtons] = useState([]);
  const [activityIndicator, setActivityIndicator] = useState(false);
  const signingUp = useRef(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();

  const myAlert = (message) => {
    setAlertModalTitle("Message");
    setAlertModalMessage(message);
    setAlertModalButtons([{ Title: "OK", Method: null }]);
    setAlertModalShow(true);
  };

  const valid_email = (email) => {
    const reg = /^[A-Z0-9._%+-]+@([A-Z0-9-]+\.)+[A-Z]{2,4}$/i;
    if (reg.test(email)) return true;
    myAlert("Please enter valid email.");
    return false;
  };

  let _uid = null;
  const pendingProfile = useRef(null);
  const profileRepairTried = useRef(false);

  const loginCallback = (res) => {
    console.log("loginCallback", res);
    if (res.pass === true) {
      let user_info = new User();
      let _user_info = user_info.data;
      _user_info.uid = res.res.user.uid;
      _user_info.email = res.res.user.email;
      pendingProfile.current = _user_info;
      dispatch(slice1Actions.setUserInfo({ value: _user_info }));
      DataBase.findUserByUID(res.res.user.uid, readUserCallback);
    } else {
      setActivityIndicator(false);
      myAlert("Login Error");
    }
  };

  const readUserCallback = (res) => {
    console.log("readUserCallback", res);
    // Signed in, but no profile document exists (a registration that was
    // interrupted before its profile write, or an account created outside
    // this flow) — create it now rather than leaving the account unusable.
    if (res.pass !== true && res.res === "No user found" && pendingProfile.current && !profileRepairTried.current) {
      profileRepairTried.current = true;
      // Don't leave the "guest"/"user" defaults as the real account's name —
      // use the auth display name if there is one, else the email's local
      // part (editable later in the profile dialog).
      const authUser = DataBase.getCurrentUser();
      const nameParts = (authUser?.displayName || "").trim().split(/\s+/).filter(Boolean);
      pendingProfile.current = {
        ...pendingProfile.current,
        first_name: nameParts[0] || (pendingProfile.current.email || "").split("@")[0],
        last_name: nameParts.slice(1).join(" "),
      };
      User.addUser(pendingProfile.current, (addRes) => {
        if (addRes.pass === true) {
          _uid = addRes.res;
          DataBase.findUserByUID(pendingProfile.current.uid, readUserCallback);
        } else {
          console.log("Profile repair failed", addRes);
          setActivityIndicator(false);
          myAlert("Read User Error: couldn't create your profile" + (addRes.res?.message ? ` (${addRes.res.message})` : ""));
        }
      });
      return;
    }
    setActivityIndicator(false);
    if (res.pass === true) {
      let user_info = res.res;
      if (_uid === null) _uid = res.doc_id;
      dispatch(slice1Actions.setUserInfo({ value: user_info }));
      dispatch(slice1Actions.setUserInfoId({ value: _uid }));
      // Matches RN's Login.js, which navigates to the Itinerary tab after
      // both a sign-in and a registration — this modal can be opened from
      // anywhere (You screen, TripAI's inline link, the sample-guide
      // banner), and previously just closed in place, leaving the user
      // wherever they happened to be rather than landing somewhere with
      // actual content.
      dispatch(slice1Actions.setGuidesOrPlaces({ value: "Places", GuideType: "None" }));
      dispatch(slice1Actions.setCurrentAppScreen({ value: "Home" }));
      setTimeout(RefreshAfterLogin, 500);
      if (props.callback != null) props.callback();
      props.onHide();
    } else myAlert("Read User Error");
  };

  const RefreshAfterLogin = () => {
    dispatch(slice1Actions.setGuide({ guide: null }));
  };

  const Login = () => {
    if (!valid_email(email)) return false;
    if (password === "") { myAlert("Please enter Password."); return false; }
    setActivityIndicator(true);
    DataBase.Login(email, password, loginCallback);
  };

  const ForgotPassword = () => {
    if (valid_email(email)) DataBase.forgotPassword(email, forgotPasswordCallback);
    else myAlert("Email is not valid");
  };

  const forgotPasswordCallback = (res) => {
    if (res === true) myAlert("Please check your email...");
    else {
      let msg = typeof res.message != "undefined" ? res.message.toString() : "Unknown error";
      myAlert("Error resetting password: " + msg);
    }
  };

  const Register = () => {
    if (!valid_email(email)) return false;
    if (password === "") { myAlert("Please enter Password."); return false; }
    // Ignore a second click while a registration is in flight or already
    // succeeded: the first call creates the account and signs the user in,
    // so a second one fails "email-already-in-use" for an email that was in
    // fact free a moment earlier.
    if (signingUp.current) return false;
    signingUp.current = true;
    DataBase.SignUp(email, password, SignUpCallback);
    setActivityIndicator(true);
  };

  const SignUpCallback = (res) => {
    if (res.pass == false) {
      signingUp.current = false;
      setActivityIndicator(false);
      const code = res.res?.code || "";
      // Already signed in as this very email (an earlier submit created the
      // account and its own chain is finishing) — not a real conflict.
      const signedInEmail = DataBase.getCurrentUser()?.email;
      if (code === "auth/email-already-in-use" && signedInEmail && signedInEmail.toLowerCase() === (email || "").trim().toLowerCase()) return;
      myAlert(
        code === "auth/email-already-in-use"
          ? "This email address is already registered. Try logging in instead."
          : "Sign Up Error" + (res.res?.message ? `: ${res.res.message}` : "")
      );
      return;
    }
    let user_info = new User();
    let _user_info = user_info.data;
    _user_info.uid = res.res.user.uid;
    _user_info.email = res.res.user.email;
    _user_info.first_name = firstName;
    _user_info.last_name = lastName;
    dispatch(slice1Actions.setUserInfo({ value: _user_info }));
    addUser(_user_info);
  };

  const addUser = (_user_info) => {
    if (_user_info.uid == null) {
      setActivityIndicator(false);
      return;
    }
    User.addUser(_user_info, addUserCallback);
  };

  const addUserCallback = (res) => {
    if (res.pass === true) {
      _uid = res.res;
      setTimeout(LoginAfterAddUser, 1000);
      return;
    }
    setActivityIndicator(false);
    myAlert("Error adding user");
  };

  const LoginAfterAddUser = () => {
    DataBase.Login(email, password, loginCallback);
  };

  // Lets the modal's own links switch mode in place (no close/reopen via
  // the menu) — mode itself is owned by the caller (HomeHeader.js/
  // MobileBottomNavBar.js's LoginModalMode), so this just asks it to
  // re-render with a different mode prop; the email the user already typed
  // survives automatically since this component instance stays mounted.
  // Password is cleared explicitly — reusing it across modes would be a
  // surprising (and slightly unsafe) default. Found/added 2026-09-14.
  const switchMode = (newMode) => {
    setPassword("");
    if (props.onSwitchMode) props.onSwitchMode(newMode);
  };

  const modeIcon =
    props.mode === "Register" ? icoRegister :
    props.mode === "Forgot Password" ? icoForgotPw :
    icoLogin;

  return (
    <div className="modal">
      <Modal
        {...props}
        animation={false}
        dialogClassName="lm-dialog"
        aria-labelledby="lm-title"
        centered
        onHide={props.onHide}
      >
        <Modal.Body style={{ padding: 0 }}>
          <div className="lm-shell">

            {/* ── Header ── */}
            <div className="lm-header">
              <div className="lm-header-icon">
                <FontAwesomeIcon icon={modeIcon} />
              </div>
              <h2 className="lm-title" id="lm-title">{props.mode}</h2>
              <button className="lm-close" onClick={props.onHide}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            {/* ── Body ── */}
            <div className="lm-body">
              <div className="lm-logo-wrap">
                <img src={img} alt="Andiamo" className="lm-logo" />
              </div>

              {activityIndicator ? (
                <div className="lm-loading">
                  <ReactLoading type="spin" color="#2563EB" height={32} width={32} />
                </div>
              ) : (
                <div className="lm-form">
                  <div className="lm-field">
                    <label className="lm-label" htmlFor="lm-email">Email Address</label>
                    <input
                      id="lm-email"
                      type="email"
                      className="lm-input"
                      placeholder="name@example.com"
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  {props.mode !== "Forgot Password" && (
                    <div className="lm-field">
                      <label className="lm-label" htmlFor="lm-password">Password</label>
                      <div className="lm-password-wrap">
                        <input
                          id="lm-password"
                          type={showPassword ? "text" : "password"}
                          className="lm-input lm-input-password"
                          placeholder="Password"
                          onChange={(e) => setPassword(e.target.value)}
                        />
                        <button
                          type="button"
                          className="lm-toggle-btn"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <FontAwesomeIcon icon={showPassword ? icoEyeSlash : icoEye} />
                        </button>
                      </div>
                    </div>
                  )}

                  {props.mode === "Register" && (
                    <>
                      <div className="lm-field">
                        <label className="lm-label" htmlFor="lm-first-name">First Name (optional)</label>
                        <input
                          id="lm-first-name"
                          type="text"
                          className="lm-input"
                          placeholder="First Name"
                          onChange={(e) => setFirstName(e.target.value)}
                        />
                      </div>
                      <div className="lm-field">
                        <label className="lm-label" htmlFor="lm-last-name">Last Name (optional)</label>
                        <input
                          id="lm-last-name"
                          type="text"
                          className="lm-input"
                          placeholder="Last Name"
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </div>
                    </>
                  )}

                  {props.mode === "Login" && (
                    <button className="lm-action" onClick={Login}>Login</button>
                  )}
                  {props.mode === "Forgot Password" && (
                    <button className="lm-action" onClick={ForgotPassword}>Forgot Password</button>
                  )}
                  {props.mode === "Register" && (
                    <button className="lm-action" onClick={Register}>Register</button>
                  )}

                  {props.mode === "Login" && (
                    <div className="lm-switch-links">
                      <button type="button" className="lm-link" onClick={() => switchMode("Forgot Password")}>Forgot password?</button>
                      <button type="button" className="lm-link" onClick={() => switchMode("Register")}>Don't have an account? Register — it's free</button>
                    </div>
                  )}
                  {props.mode === "Register" && (
                    <div className="lm-switch-links">
                      <button type="button" className="lm-link" onClick={() => switchMode("Login")}>Already have an account? Log in</button>
                    </div>
                  )}
                  {props.mode === "Forgot Password" && (
                    <div className="lm-switch-links">
                      <button type="button" className="lm-link" onClick={() => switchMode("Login")}>Back to Login</button>
                    </div>
                  )}
                </div>
              )}
            </div>

          </div>
        </Modal.Body>
      </Modal>
      <AlertModal
        show={AlertModalShow}
        onHide={() => setAlertModalShow(false)}
        AlertModalTitle={AlertModalTitle}
        AlertModalMessage={AlertModalMessage}
        AlertModalButtons={AlertModalButtons}
      />
    </div>
  );
};

export default LoginModal;
